import React, {useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { SUCCESS, TOAST_SUCCESS, TOAST_ERROR, BUTTON_PRIMARY, PARCEL_USE_CASE } from '../assets/constants/Constants';
import { getSecureItemFromSpecificStorage, removeSecureItemFromSpecificStorage, getAndThenRemoveSecureItemFromSpecificStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY, LS_PROP_LOCKER_UNIT_DETAILS, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT, LS_CUSTOMER } from '../assets/constants/BrowserStorageKeys';
import {PARCEL_ICON} from '../assets/constants/Icons';
import { ACCESS_LOCKER_BANK, HOME, SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED } from '../assets/constants/PageList';
import { getPathForNextPage, verifyUserProximity, getUnitLabelParams } from '../lib/Utils';
import {openLocker, releaseLockerOfPickUpUser} from '../api/api';
import { postPaymentSaveTransaction } from '../lib/BackendUtils';
import CustomDialog from '../components/CustomDialog';
import CustomSliderComponent from '../components/CustomSliderComponent';
import CircularProgressLoader from '../components/CircularProgressLoader';
import ShowToast from '../components/ToastComponent';

const SwipeToOpenPage = () => {
    const history = useHistory();
    const {t} = useTranslation();
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const isRedirectedFromStripePaymentGateway = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY);
    const lockerUnitDetails = history.location?.state?.lockerUnitDetails?.ref_id ? history.location?.state?.lockerUnitDetails : getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_PROP_LOCKER_UNIT_DETAILS);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [userProximityError, setUserProximityError] = useState('');
    const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);
    const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const isGeoFencingEnabled = tenantAndAssignmentSettings?.is_geofencing;
    const lockerBankLatitude = lockerUnitDetails?.locker_bank?.latitude ? lockerUnitDetails?.locker_bank?.latitude : lockerBankInfo.latitude;
    const lockerBankLongitude = lockerUnitDetails?.locker_bank?.longitude ? lockerUnitDetails?.locker_bank?.longitude : lockerBankInfo.longitude;
    const canOpenLockerAutomaticallyWhenPageLoads = tenantAndAssignmentSettings?.usecase_settings?.skip_swipe_to_open?.value;
    const isOpenWithCheckoutAfterOverstayPayment = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT);
    const isOpenWithCheckoutButtonClicked = history.location?.state?.isOpenWithCheckoutButtonClicked ? history.location?.state?.isOpenWithCheckoutButtonClicked : false;
    removeSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT);
    const useCase = tenantAndAssignmentSettings?.usecase;
    const unitLabelParams = getUnitLabelParams(lockerUnitDetails?.locker_bank?.bank_type ?? lockerBankInfo?.LockerBankType, t);

    useEffect(() => {
        handleFlagBasedLockerFunctionalitiesUponInitialPageLoad();
    }, []);

    const handleFlagBasedLockerFunctionalitiesUponInitialPageLoad = async () => {
        if (isRedirectedFromStripePaymentGateway) {
            await savePostPaymentTransactionDetailsWhenRedirectedFromStripe();
        }

        if (canOpenLockerAutomaticallyWhenPageLoads) {
            if (isOpenWithCheckoutAfterOverstayPayment) {
                openLockerAndThenCheckout();
            }
            else {
                slideToOpenButtonHandler();
            }
        }
        else {
            setIsLoading(false);
        }
    };

    const savePostPaymentTransactionDetailsWhenRedirectedFromStripe = async () => {
        const body = {
            payment_reference_id: lockerUnitDetails?.payment_records[0]?.transaction_id,
            payment_id: lockerUnitDetails?.payment_records[0]?.payment_gateway_reference,
            order_id: "",
            payment_signature: "signature",
            payment_platform : "pwa",
        };

        const postPaymentTransactionResponse = await postPaymentSaveTransaction(body);

        if (postPaymentTransactionResponse.status === SUCCESS) {
            removeSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY);
        }
    }

    const slideToOpenButtonHandler = async () => {
        setLoadingMessage(t('opening_locker_please_wait', unitLabelParams));
        setIsLoading(true);
        
        if (isGeoFencingEnabled) {
            const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        try {
            const openLockerResponse = await openLocker(lockerUnitDetails?.ref_id);
            if (openLockerResponse.status === SUCCESS) {
                if(isOpenWithCheckoutButtonClicked) {
                    setLoadingMessage(t('checkoutInProgress'));
                    const releaseLockerResponse = await releaseLockerOfPickUpUser(lockerUnitDetails?.ref_id);
                    if (releaseLockerResponse?.status === SUCCESS) {
                        setIsLoading(false);
                        ShowToast(t('lockerOpenedReleased', unitLabelParams), TOAST_SUCCESS);
                    }else{
                        setIsLoading(false);
                        ShowToast(releaseLockerResponse?.message, TOAST_ERROR);
                    }
                }else{
                    setLoadingMessage(t('opening_locker_please_wait', unitLabelParams));
                if(tenantAndAssignmentSettings?.usecase_settings?.show_pickup_btn_pwa?.value)
                {
                    setLoadingMessage(t('lockerReleasing', unitLabelParams));
                    const releaseLockerResponse = await releaseLockerOfPickUpUser(
                        lockerUnitDetails?.ref_id
                );
                }else{
                    ShowToast(openLockerResponse.message, TOAST_SUCCESS);
                }
                }
                history.replace({
                    pathname: getPathForNextPage(SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED),
                    state: {
                        lockerUnitDetails
                    }
                });
            }else{
                setIsLoading(false);
                ShowToast(openLockerResponse.message, TOAST_ERROR);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while opening locker: ', error);
        }
    };

    const openLockerAndThenCheckout = async () => {
        setLoadingMessage(t('opening_locker_please_wait', unitLabelParams));
        setIsLoading(true);

        sessionStorage.removeItem('isOpenWithCheckoutAfterOverstayPayment');

        if (isGeoFencingEnabled) {
            const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        try {
            const openLockerResponse = await openLocker(lockerUnitDetails?.ref_id);

            if (openLockerResponse?.status === SUCCESS) {
                setLoadingMessage(t('checkoutInProgress'));

                const releaseLockerResponse = await releaseLockerOfPickUpUser(lockerUnitDetails?.ref_id);

                if (releaseLockerResponse?.status === SUCCESS) {
                    ShowToast(releaseLockerResponse?.message, TOAST_SUCCESS);
                    setIsLoading(false);
                    history.push({
                        pathname: getPathForNextPage(SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED),
                        state: {
                            lockerUnitDetails
                        }
                    });
                }
            }
        } catch (error) {
            ShowToast(t(`Error_LockerOpening_Checkout`), TOAST_ERROR);
            console.error('Error during open with checkout: ', error);
            setIsLoading(false);

            history.push({
                pathname: getPathForNextPage(ACCESS_LOCKER_BANK, HOME),
            });
        }
    }

    return (
        <Box className='swipe-to-open-page'>
            <Box className='main-container'>
                {isLoading && (
                    <Box className='main'>
                        <CircularProgressLoader message={loadingMessage} />
                    </Box>
                )}
                {(!isLoading) && (
                    <Box className='main'>
                        <Box className='locker-icon-container'>
                            <PARCEL_ICON />
                        </Box>
                        <Typography className='locker-bank-name'>{lockerUnitDetails?.locker_bank?.name}</Typography>
                        <Typography className='locker-unit'>{lockerUnitDetails?.locker_door?.door_no}</Typography>
                        <Box className='slide-to-open-button-container'>
                            <CustomSliderComponent
                                onSlideDone={slideToOpenButtonHandler}
                                extraClass={'slide-to-open-button'}
                            />
                        </Box>
                        <CustomDialog
                            dialogId='user-proximity-error-modal'
                            dialogVisible={isUserProximityErrorModalVisible}
                            onClose={() => setIsUserProximityErrorModalVisible(false)}
                            handleCancel={() => setIsUserProximityErrorModalVisible(false)}
                            dialogTitle={t('Location_error')}
                            dialogContentText={userProximityError}
                            buttonOneTitle={t('button_ok')}
                            buttonProps={{buttonOne: {type: BUTTON_PRIMARY}}}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SwipeToOpenPage;